import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Star, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SubmitReview() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    role: 'Teacher',
    review: '',
    rating: 5
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const roles = ['Teacher', 'Parent', 'Student'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.review.length < 20) {
      toast.error('Please write at least 20 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, formData);
      setSubmitted(true);
      toast.success('Thank you for your review!');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Navigation />
        <div className="flex items-center justify-center py-20 px-6">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Review Submitted!
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Thank you for sharing your experience. Your review will help other teachers!
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                {[...Array(formData.rating)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Share Your Experience
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whether you've worked with me as a tutor or used the teaching resources, I'd love to hear your feedback. Your testimonial helps other students, parents, and teachers!
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-base font-semibold text-slate-900 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter your full name"
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-900 mb-2">
                  Your Role *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {roles.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setFormData({ ...formData, role })}
                      className={`py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                        formData.role === role
                          ? 'border-blue-600 bg-blue-50 text-blue-600'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-900 mb-2">
                  Your Rating *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= formData.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  {formData.rating === 5 && '⭐ Excellent!'}
                  {formData.rating === 4 && '⭐ Great!'}
                  {formData.rating === 3 && '👍 Good'}
                  {formData.rating === 2 && '👌 Fair'}
                  {formData.rating === 1 && '💭 Needs improvement'}
                </p>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-900 mb-2">
                  Your Review *
                </label>
                <textarea
                  value={formData.review}
                  onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                  required
                  rows={6}
                  placeholder="Share your experience: How did the tutoring help? How have the teaching resources supported your learning/teaching? What did you appreciate most?"
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                />
                <p className="text-sm text-slate-500 mt-2">
                  {formData.review.length} characters (minimum 20)
                </p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-4">
                <p className="text-sm text-slate-600">
                  <strong>Note:</strong> Your review will be published on the Learn with Zeenat website to help other students, parents, and teachers.
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting || formData.review.length < 20}
                className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-6 py-4 font-semibold shadow-sm hover:shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Review
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default SubmitReview;
